import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Trash2, Plus, Upload, Building2, Quote } from "lucide-react";
import { SPONSOR_TIERS, TRIBUTE_TIER } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full border-b-2 border-border bg-transparent px-1 py-2 text-espresso rounded-none focus:outline-none focus:border-rust text-sm";
const TIER_LIST = [...SPONSOR_TIERS, TRIBUTE_TIER];
const TIER_OPTIONS = ["Founding Partner", ...TIER_LIST.map((t) => (t.key === "tribute" ? t.name : `${t.name} Sponsor`)), "Activation Sponsor", "In-Kind Partner"];

const shrinkImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 600 / img.width, 300 / img.height);
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

export const SponsorSetup = ({ token }) => {
  const headers = { "X-Admin-Token": token };
  const [cfg, setCfg] = useState(null);
  const [capacity, setCapacity] = useState({});
  const [activations, setActivations] = useState([]);
  const [spotlights, setSpotlights] = useState({});
  const [saving, setSaving] = useState(false);
  const [partner, setPartner] = useState({ name: "", tier_label: "Founding Partner", website: "", logo_data: "" });
  const [addingPartner, setAddingPartner] = useState(false);

  const load = async () => {
    const res = await axios.get(`${API}/sponsor-config`);
    setCfg(res.data);
    setCapacity(Object.fromEntries(Object.entries(res.data.tiers).map(([k, v]) => [k, v.capacity])));
    setActivations(res.data.activations.map(({ key, label, amount, desc }) => ({ key, label, amount, desc: desc || "" })));
    setSpotlights(Object.fromEntries(TIER_LIST.map((t) => [t.key, { quote: "", by: "", company: "", ...(res.data.spotlights?.[t.key] || {}) }])));
  };
  useEffect(() => { load().catch(() => toast.error("Could not load sponsor settings.")); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/sponsor-config`, {
        tier_capacity: Object.fromEntries(Object.entries(capacity).map(([k, v]) => [k, Number(v) || 0])),
        activations: activations.map((a) => ({ ...a, amount: Number(a.amount) || 0, key: a.key || a.label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") })),
        spotlights,
      }, { headers });
      setCfg(res.data);
      toast.success("Sponsor settings saved.", { description: "Spots, prices and spotlight quotes are live on the website." });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  const addPartner = async (e) => {
    e.preventDefault();
    if (!partner.name) return toast.error("Partner name is required.");
    setAddingPartner(true);
    try {
      await axios.post(`${API}/admin/partners`, partner, { headers });
      setPartner({ name: "", tier_label: "Founding Partner", website: "", logo_data: "" });
      await load();
      toast.success("Partner added to the sponsor wall.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not add partner.");
    } finally {
      setAddingPartner(false);
    }
  };

  const removePartner = async (id) => {
    try {
      await axios.delete(`${API}/admin/partners/${id}`, { headers });
      await load();
      toast.success("Partner removed.");
    } catch (err) {
      toast.error("Could not remove partner.");
    }
  };

  if (!cfg) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-rust" /></div>;

  return (
    <div data-testid="admin-sponsor-setup" className="space-y-12">
      {/* Spots per tier */}
      <section>
        <h3 className="label-font text-xs text-rust tracking-[0.15em] border-b border-border pb-2">Spots Available Per Level</h3>
        <p className="mt-2 text-xs text-muted-foreground">Set the total spots offered. "Remaining" subtracts paid and invoiced commitments automatically.</p>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TIER_LIST.map((t) => {
            const live = cfg.tiers[t.key] || {};
            return (
              <div key={t.key} className="border border-border bg-background p-4">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-serif font-bold text-espresso">{t.name}</div>
                  <div className="text-xs text-muted-foreground">${t.price.toLocaleString()}</div>
                </div>
                <label className="mt-3 block label-font text-[10px] text-espresso/70">Total spots</label>
                <input
                  data-testid={`capacity-${t.key}`}
                  type="number" min="0"
                  className={inputCls}
                  value={capacity[t.key] ?? ""}
                  onChange={(e) => setCapacity({ ...capacity, [t.key]: e.target.value })}
                />
                <div className="mt-2 text-xs text-muted-foreground">
                  Committed: <span className="text-espresso font-medium">{(live.capacity ?? 0) - (live.remaining ?? 0)}</span> · Remaining: <span className="text-rust font-medium">{live.remaining ?? "—"}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Activations */}
      <section>
        <h3 className="label-font text-xs text-rust tracking-[0.15em] border-b border-border pb-2">Activation Sponsorships (exclusive · 1 each)</h3>
        <div className="mt-5 space-y-3">
          {activations.map((a, i) => (
            <div key={a.key || i} data-testid={`activation-row-${a.key || i}`} className="grid grid-cols-1 sm:grid-cols-[1.2fr_0.6fr_2fr_auto] gap-3 items-end border border-border bg-background p-3">
              <div>
                <label className="label-font text-[10px] text-espresso/70">Name</label>
                <input className={inputCls} value={a.label} onChange={(e) => setActivations(activations.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              </div>
              <div>
                <label className="label-font text-[10px] text-espresso/70">Price ($)</label>
                <input data-testid={`activation-price-${a.key}`} type="number" min="0" className={inputCls} value={a.amount} onChange={(e) => setActivations(activations.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} />
              </div>
              <div>
                <label className="label-font text-[10px] text-espresso/70">Description</label>
                <input className={inputCls} value={a.desc} onChange={(e) => setActivations(activations.map((x, j) => (j === i ? { ...x, desc: e.target.value } : x)))} />
              </div>
              <button type="button" onClick={() => setActivations(activations.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-rust p-2" aria-label="Remove activation"><Trash2 size={16} /></button>
            </div>
          ))}
          <button type="button" data-testid="add-activation-btn" onClick={() => setActivations([...activations, { key: "", label: "", amount: 0, desc: "" }])} className="label-font text-[11px] border border-dashed border-border px-4 py-2.5 text-espresso/70 hover:border-rust hover:text-rust flex items-center gap-2">
            <Plus size={14} /> Add Activation
          </button>
        </div>
        <button data-testid="save-sponsor-config-btn" onClick={save} disabled={saving} className="mt-6 label-font text-xs bg-rust text-primary-foreground px-6 py-3.5 border border-espresso flex items-center gap-2 transition-transform hover:-translate-y-0.5 hard-shadow-sm disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Spots & Prices
        </button>
      </section>

      {/* Sponsor Spotlight quotes */}
      <section data-testid="admin-spotlights">
        <h3 className="label-font text-xs text-rust tracking-[0.15em] border-b border-border pb-2 flex items-center gap-2"><Quote size={12} /> Sponsor Spotlight — "Why We Sponsor"</h3>
        <p className="mt-2 text-xs text-muted-foreground">Add a short quote from a real sponsor to each level. It appears inside that tier's card on the Sponsor page. Leave a level blank to show nothing.</p>
        <div className="mt-5 grid md:grid-cols-2 gap-4">
          {TIER_LIST.map((t) => {
            const s = spotlights[t.key] || { quote: "", by: "", company: "" };
            const set = (patch) => setSpotlights({ ...spotlights, [t.key]: { ...s, ...patch } });
            return (
              <div key={t.key} data-testid={`spotlight-row-${t.key}`} className="border border-border bg-background p-4">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-serif font-bold text-espresso">{t.name}</div>
                  <div className="text-[10px] text-muted-foreground">{s.quote.length}/280</div>
                </div>
                <label className="mt-3 block label-font text-[10px] text-espresso/70">Quote</label>
                <textarea
                  data-testid={`spotlight-quote-${t.key}`}
                  rows={2} maxLength={280}
                  className={`${inputCls} resize-none`}
                  placeholder="Why we sponsor the Heroes & Legacy Golf Classic…"
                  value={s.quote}
                  onChange={(e) => set({ quote: e.target.value })}
                />
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-font text-[10px] text-espresso/70">Name & title</label>
                    <input data-testid={`spotlight-by-${t.key}`} className={inputCls} maxLength={80} placeholder="Jane Doe, CEO" value={s.by} onChange={(e) => set({ by: e.target.value })} />
                  </div>
                  <div>
                    <label className="label-font text-[10px] text-espresso/70">Company</label>
                    <input data-testid={`spotlight-company-${t.key}`} className={inputCls} maxLength={80} placeholder="Acme Energy" value={s.company} onChange={(e) => set({ company: e.target.value })} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <button data-testid="save-spotlights-btn" onClick={save} disabled={saving} className="mt-6 label-font text-xs bg-rust text-primary-foreground px-6 py-3.5 border border-espresso flex items-center gap-2 transition-transform hover:-translate-y-0.5 hard-shadow-sm disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Spotlights
        </button>
      </section>

      {/* Partners */}
      <section>
        <h3 className="label-font text-xs text-rust tracking-[0.15em] border-b border-border pb-2">Sponsor Wall — Founding Partners</h3>
        <p className="mt-2 text-xs text-muted-foreground">Partners added here appear instantly on the Sponsor page wall. Until there's at least one, the site shows a "Become a Founding Partner" call-to-action instead.</p>
        <form onSubmit={addPartner} className="mt-5 grid sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1.2fr_auto_auto] gap-4 items-end border border-border bg-background p-4">
          <div>
            <label className="label-font text-[10px] text-espresso/70">Company name *</label>
            <input data-testid="partner-name" className={inputCls} value={partner.name} onChange={(e) => setPartner({ ...partner, name: e.target.value })} placeholder="Acme Energy" />
          </div>
          <div>
            <label className="label-font text-[10px] text-espresso/70">Recognition</label>
            <select data-testid="partner-tier" className={inputCls} value={partner.tier_label} onChange={(e) => setPartner({ ...partner, tier_label: e.target.value })}>
              {TIER_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="label-font text-[10px] text-espresso/70">Website (optional)</label>
            <input data-testid="partner-website" className={inputCls} value={partner.website} onChange={(e) => setPartner({ ...partner, website: e.target.value })} placeholder="https://" />
          </div>
          <label className="label-font text-[11px] border border-espresso px-4 py-2.5 text-espresso flex items-center gap-2 cursor-pointer hover:bg-espresso/5 whitespace-nowrap">
            <Upload size={14} /> {partner.logo_data ? "Logo ready" : "Logo"}
            <input data-testid="partner-logo" type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setPartner({ ...partner, logo_data: await shrinkImage(f) }); }} />
          </label>
          <button data-testid="add-partner-btn" type="submit" disabled={addingPartner} className="label-font text-[11px] bg-espresso text-cream px-5 py-2.5 border border-espresso flex items-center gap-2 hard-shadow-sm disabled:opacity-60 whitespace-nowrap">
            {addingPartner ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add Partner
          </button>
        </form>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cfg.partners.map((p) => (
            <div key={p.id} data-testid={`admin-partner-${p.id}`} className="border border-border bg-background p-4 flex flex-col">
              <div className="h-16 flex items-center justify-center bg-espresso/5">
                {p.logo_data ? <img src={p.logo_data} alt={p.name} className="max-h-12 max-w-[85%] object-contain" /> : <Building2 className="text-espresso/40" />}
              </div>
              <div className="mt-3 font-serif font-bold text-espresso leading-tight">{p.name}</div>
              <div className="label-font text-[9px] text-rust mt-1">{p.tier_label}</div>
              <button onClick={() => removePartner(p.id)} data-testid={`remove-partner-${p.id}`} className="mt-3 self-start text-xs text-muted-foreground hover:text-rust flex items-center gap-1"><Trash2 size={12} /> Remove</button>
            </div>
          ))}
          {cfg.partners.length === 0 && <p className="text-sm text-muted-foreground">No partners yet — the website shows the Founding Partner call-to-action.</p>}
        </div>
      </section>
    </div>
  );
};
